import { useEffect, useState } from 'react';
import { Maximize, Minimize, RotateCcw } from 'lucide-react';
import { MEMORY_FRAGMENTS, STORY_ACTS, actOf, glossLine } from '@/show/saysConfig';
import { FEELING_COLORS3 } from '@/show/freeConfig';
import { Transmission } from './StageHud';

const pad = (n) => String(n).padStart(2, '0');

// one memory fragment per decoded experiment: a classified evidence card
export const MemoryFragmentCard = ({ snap }) => {
  const f = snap.levelFeeling;
  const act = actOf(snap.levelIdx);
  return (
    <div data-testid="memory-card-wrap" className="pointer-events-none absolute inset-x-0 top-[27%] flex justify-center">
      <div data-testid="memory-card" data-feeling={f} data-step={snap.memStep} className={`memory-card w-[min(1040px,62vw)] px-8 py-6 lg:px-10 lg:py-8 ${snap.memStep === 'fadeout' ? 'memory-card-out' : ''}`}>
        <div className="flex justify-between gap-6 whitespace-nowrap text-[0.6rem] tracking-[0.3em] text-[var(--muted)] lg:text-sm">
          <span data-testid="memory-card-title">MEMORY FRAGMENT {pad(snap.level)}/{snap.feelings.length} · SUBJECT: M. CHAMPIGNON</span>
          <span>ACT {act.roman} · {act.title}</span>
        </div>
        <div className="mt-2 h-px bg-[var(--amber)]/40" />
        <div data-testid="memory-card-text" className="memory-type mt-5 text-xl leading-snug text-[var(--ink)] lg:text-2xl 2xl:text-4xl" style={{ fontFamily: 'var(--font-mono)' }}>{MEMORY_FRAGMENTS[f]}</div>
        <div className="mt-5 flex items-end justify-between lg:mt-6">
          <span className="text-xs tracking-[0.3em] text-[var(--dim)]">SOURCE: SPECIMEN: THE SPORE<span className="normal-case">s</span> · SPORE<span className="normal-case">s</span> RECALL · {f.toUpperCase()}</span>
          <span className="stamp memory-stamp px-4 py-1 text-lg tracking-[0.3em]">RECOVERED</span>
        </div>
      </div>
    </div>
  );
};

export const FinaleRequest = () => (
  <div data-testid="finale-request" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4">
    <div className="label-eyebrow">GROUND FLOOR · SPORE LAB · GRAY&apos;S INN ROAD</div>
    <div className="font-display text-6xl font-black tracking-[0.06em] lg:text-8xl">ZE <span className="text-[var(--amber)]">FOURTH</span> FLOOR</div>
    <div className="label-eyebrow text-[var(--dim)]">A REQUEST FROM PR. H. CHAMPIGNON</div>
  </div>
);

const useElapsed = () => {
  const [t0] = useState(() => Date.now());
  const [s, setS] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setS(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [t0]);
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
};

// the assistants are upstairs; no Henri calls while waiting, only SPORE's ambience
export const SearchScreen = ({ onFound }) => {
  const elapsed = useElapsed();
  return (
    <div data-testid="search-screen" className="absolute inset-0 z-20 flex flex-col justify-between px-16 py-14">
      <div className="search-scan pointer-events-none absolute inset-0" />
      <div className="relative flex justify-between label-eyebrow">
        <span>[ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · SIGNAL: DORMANT</span>
        <span data-testid="search-elapsed">ELAPSED {elapsed}</span>
      </div>
      <div className="relative flex flex-col items-center gap-6 text-center">
        <div className="dot-live dot-pulse h-4 w-4" style={{ background: '#ff5a6e' }} />
        <div data-testid="search-title" className="font-display text-5xl font-black tracking-[0.1em] lg:text-7xl">RESEARCH ASSISTANTS DISPATCHED · <span className="text-[var(--amber)]">4TH FLOOR</span></div>
        <div className="label-eyebrow text-base text-[var(--muted)]">OBJECTIVE: SKELETAL REMAINS · IDENTIFIER: ROBIN BROOCH · RETURN TO GROUND FLOOR</div>
        <button type="button" data-testid="brooch-found-button" onClick={onFound} className="landing-btn mt-10 border-2 border-[var(--amber)] bg-[var(--panel-2)]/80 px-16 py-10 font-display text-5xl font-black tracking-[0.06em] text-[var(--amber)] transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-[#ffb00018] active:scale-95 lg:text-7xl">
          WE FOUND THE ROBIN BROOCH
        </button>
      </div>
      <div className="relative label-eyebrow text-right text-[var(--dim)]">CONTAINMENT BREACH · OUTBREAK OF THE DARKNESS · FLOOR 4 SEALED SINCE</div>
    </div>
  );
};

export const BroochReveal = ({ snap }) => (
  <div data-testid="brooch-reveal" className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-8">
    <img data-testid="brooch-image" src="/story/robin_brooch.jpg" alt="Margaux's robin brooch" className="brooch-img h-[42vh] w-[42vh] object-cover" />
    <div className="label-eyebrow">EVIDENCE 13/12 · ROBIN BROOCH · RECOVERED FROM FLOOR 4 · PROPERTY OF DR M. CHAMPIGNON</div>
    <div className="w-[min(900px,70vw)]"><Transmission tx={snap.ui.transmission} /></div>
  </div>
);

export const MemoryFile = ({ snap }) => (
  <div data-testid="memory-file" className="border border-[var(--amber)]/50 p-3 text-left 2xl:p-5">
    <div className="label-eyebrow mb-1.5 flex justify-between gap-6 text-[0.6rem] text-[var(--amber)] 2xl:text-xs"><span>MARGAUX&apos;S MEMORY FILE · CLASSIFIED</span><span>{snap.discovered.length}/{snap.feelings.length} RECOVERED</span></div>
    {STORY_ACTS.map((a) => (
      <div key={a.n} className="mb-1 2xl:mb-2">
        <div className="text-[0.55rem] tracking-[0.35em] text-[var(--dim)] 2xl:text-[0.65rem]">ACT {a.roman} · {a.title}</div>
        {snap.feelings.slice((a.n - 1) * 4, a.n * 4).map((f, i) => {
          const n = (a.n - 1) * 4 + i + 1;
          const got = snap.discovered.includes(f);
          return (
            <div key={f} data-testid={`memory-file-${f}`} data-recovered={got} className="flex gap-3 border-b border-white/5 py-0.5 text-[0.68rem] 2xl:py-1 2xl:text-sm">
              <span className="text-[var(--muted)]">{pad(n)}</span>
              {got ? <span className="text-[var(--ink)]">{MEMORY_FRAGMENTS[f]}</span> : <span className="redacted">████████ ██████ ███ ██████████ ████</span>}
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

export const StoryEndScreen = ({ snap }) => (
  <div data-testid="end-screen" className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-[#050807]/75 px-8 pb-4 pt-12 text-center">
    <div className="label-eyebrow text-[0.6rem] 2xl:text-xs">SPORE LAB · [ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · THE DISAPPEARANCE OF MARGAUX CHAMPIGNON · CASE CLOSED</div>
    <h2 data-testid="end-count" className="font-display mt-1 text-3xl font-black 2xl:mt-3 2xl:text-6xl">{snap.discovered.length}/{snap.feelings.length} <span className="glow-spore">FEELINGS DECODED</span></h2>
    <div className="mt-3 grid w-[min(1760px,94vw)] grid-cols-2 gap-5 2xl:mt-5">
      <div data-testid="end-lab-log" className="border border-[var(--hair-strong)] p-3 text-left 2xl:p-5">
        <div className="label-eyebrow mb-1.5 flex justify-between gap-6 text-[0.6rem] 2xl:text-xs"><span>ZE MYCOLOGIST&apos;S FIELD DICTIONARY</span><span>HIVE ROOTS · FEELING</span></div>
        {snap.discovered.map((f) => (
          <div key={f} data-testid={`end-log-${f}`} className="flex items-baseline justify-between gap-3 border-b border-white/5 py-0.5 text-[0.68rem] 2xl:py-1 2xl:text-sm">
            <span className="truncate text-[var(--amber)]">EXP {pad(snap.feelings.indexOf(f) + 1)} · {snap.guesses[f] || '—'} (?)</span>
            <span className="shrink-0 whitespace-nowrap text-right">
              <span data-testid={`end-gloss-${f}`} className="mr-2 text-[0.6rem] text-[var(--muted)] 2xl:text-xs">{glossLine(f)}</span>
              <span className="font-bold tracking-[0.2em]" style={{ color: FEELING_COLORS3[f] }}>{f.toUpperCase()}</span>
            </span>
          </div>
        ))}
      </div>
      <MemoryFile snap={snap} />
    </div>
  </div>
);

// small, touch-friendly corner controls on every screen
export const CornerControls = ({ armed, onReset, onFullscreen }) => {
  const [fs, setFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const on = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);
  const tap = (fn) => (e) => {
    e.stopPropagation();
    e.currentTarget.blur();
    fn();
  };
  return (
    <div data-testid="corner-controls" className="corner-controls absolute right-2 top-2 z-[60] flex gap-1.5">
      <button type="button" data-testid="screen-reset-button" data-armed={armed} onClick={tap(onReset)} className={`corner-btn ${armed ? 'corner-btn-armed' : ''}`} aria-label="Reset show">
        <RotateCcw size={16} />
        {armed && <span data-testid="screen-reset-confirm" className="ml-1.5 text-[0.65rem] tracking-[0.2em]">TAP AGAIN TO RESET</span>}
      </button>
      <button type="button" data-testid="screen-fullscreen-button" onClick={tap(onFullscreen)} className="corner-btn" aria-label="Toggle fullscreen">
        {fs ? <Minimize size={16} /> : <Maximize size={16} />}
      </button>
    </div>
  );
};
